import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { RestStatus } from '../services/api';

@customElement('rest-status-badge')
export class RestStatusBadge extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  status: RestStatus | null = null;

  @property({ type: Boolean })
  showDetails = false;

  @property({ type: Boolean })
  compact = false;

  private formatShortDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  render() {
    if (!this.status) {
      return html`<span class="badge badge-ghost">--</span>`;
    }

    const { status, rest_days_needed, rest_days_elapsed, available_date, last_pitch_count } = this.status;
    const daysRemaining = Math.max(0, Math.min(rest_days_needed, rest_days_needed - rest_days_elapsed));

    switch (status) {
      case 'available':
        return this.renderAvailable();
      case 'resting':
        return this.renderResting(daysRemaining, available_date, last_pitch_count);
      case 'limited':
        return this.renderLimited(daysRemaining, available_date);
      default:
        return html`<span class="badge badge-ghost">${status}</span>`;
    }
  }

  private renderAvailable() {
    if (this.compact) {
      return html`
        <span class="badge badge-success badge-sm">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </span>
      `;
    }

    return html`
      <div class="flex items-center gap-1">
        <span class="badge badge-success gap-1">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
          Available
        </span>
      </div>
    `;
  }

  private renderResting(daysRemaining: number, availableDate: string | undefined, lastPitchCount: number) {
    if (this.compact) {
      return html`
        <span class="badge badge-error badge-sm gap-1">
          ${availableDate ? this.formatShortDate(availableDate) : `${daysRemaining}d`}
        </span>
      `;
    }

    return html`
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-1">
          <span class="badge badge-error gap-1">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
            ${daysRemaining}d rest required
          </span>
        </div>
        ${this.showDetails
          ? html`
              <div class="text-xs text-base-content/60 space-y-0.5">
                <div>Last game: ${lastPitchCount} pitches</div>
                ${availableDate ? html`<div>Available: ${this.formatShortDate(availableDate)}</div>` : nothing}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private renderLimited(daysRemaining: number, availableDate: string | undefined) {
    if (this.compact) {
      return html`
        <span class="badge badge-warning badge-sm gap-1">
          <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          ${availableDate ? this.formatShortDate(availableDate) : nothing}
        </span>
      `;
    }

    return html`
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-1">
          <span class="badge badge-warning gap-1">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
            Limited
          </span>
        </div>
        ${this.showDetails && availableDate
          ? html`
              <div class="text-xs text-base-content/60">
                <div>Full availability: ${this.formatShortDate(availableDate!)}</div>
                <div>(${daysRemaining} days)</div>
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rest-status-badge': RestStatusBadge;
  }
}
